import { Quote } from '../types';
import { QUOTE_EDITABLE_KEYS } from './quoteEditorSave';

/**
 * Lokale Sicherung eines ungespeicherten Angebotsentwurfs (localStorage).
 *
 * Schlaegt das Speichern fehl oder geht der Tab zu, lebte der Entwurf bisher
 * nur im Speicher. Jetzt liegt er zusaetzlich im Browser und wird beim
 * naechsten Oeffnen des Angebots zur Wiederherstellung angeboten.
 *
 * Jeder Speicherzugriff ist gekapselt: privater Modus, volles Kontingent oder
 * gesperrte Website-Daten duerfen den Editor nie zum Absturz bringen.
 */
export interface QuoteDraftBackup {
  quoteId: string;
  /** updatedAt des Serverstands, auf dem der Entwurf aufsetzt. */
  baseUpdatedAt: string | null;
  draft: Quote;
  /**
   * Der Serverstand, von dem aus `draft` bearbeitet wurde. Damit legt die
   * Wiederherstellung nur die EIGENEN Aenderungen auf den aktuellen Server
   * (Drei-Wege), statt alte, nie angefasste Felder zurueckzuschreiben.
   * Fehlt bei Altsicherungen.
   */
  base?: Quote | null;
  /** ISO-Zeitpunkt der Sicherung. */
  savedAt: string;
  /** Editor-Sitzung, die gesichert hat (je geoeffnetem Editor eine). */
  sessionId?: string;
  /** Laufende Nummer der Sicherung innerhalb der Sitzung. */
  seq?: number;
}

export interface BackupStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const QUOTE_DRAFT_BACKUP_PREFIX = 'osia.quoteDraftBackup.v1:';

/**
 * Zwei Plaetze je Angebot:
 *  - 'main'    — die normale Sicherung;
 *  - 'pending' — die Sicherung der LAUFENDEN Sitzung, solange eine aeltere
 *                Sicherung (main) noch zur Wiederherstellung angeboten wird und
 *                deshalb nicht ueberschrieben werden darf.
 * Beim Oeffnen wird die juengere der beiden angeboten.
 */
export type BackupSlot = 'main' | 'pending';

export const quoteDraftBackupKey = (quoteId: string, slot: BackupSlot = 'main'): string =>
  `${QUOTE_DRAFT_BACKUP_PREFIX}${quoteId}${slot === 'pending' ? ':pending' : ''}`;

function defaultStorage(): BackupStorage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

function resolveStorage(storage: BackupStorage | null | undefined): BackupStorage | null {
  return storage === undefined ? defaultStorage() : storage;
}

export function writeQuoteDraftBackup(
  backup: QuoteDraftBackup,
  storage?: BackupStorage | null,
  slot: BackupSlot = 'main',
): boolean {
  const s = resolveStorage(storage);
  if (!s) return false;
  try {
    s.setItem(quoteDraftBackupKey(backup.quoteId, slot), JSON.stringify(backup));
    return true;
  } catch {
    return false;
  }
}

function isBackup(value: unknown, quoteId: string): value is QuoteDraftBackup {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const draft = v.draft as Record<string, unknown> | null | undefined;
  return (
    v.quoteId === quoteId &&
    typeof v.savedAt === 'string' &&
    !Number.isNaN(Date.parse(v.savedAt)) &&
    (v.baseUpdatedAt === null || typeof v.baseUpdatedAt === 'string') &&
    !!draft && typeof draft === 'object' && draft.id === quoteId &&
    (v.base === undefined || v.base === null ||
      (typeof v.base === 'object' && (v.base as Record<string, unknown>).id === quoteId)) &&
    (v.sessionId === undefined || typeof v.sessionId === 'string') &&
    (v.seq === undefined || typeof v.seq === 'number')
  );
}

export function readQuoteDraftBackup(
  quoteId: string,
  storage?: BackupStorage | null,
  slot: BackupSlot = 'main',
): QuoteDraftBackup | null {
  const s = resolveStorage(storage);
  if (!s) return null;
  try {
    const raw = s.getItem(quoteDraftBackupKey(quoteId, slot));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isBackup(parsed, quoteId) ? parsed : null;
  } catch {
    return null;
  }
}

/** Einen Platz (oder ohne `slot`: beide) leeren. */
export function clearQuoteDraftBackup(quoteId: string, storage?: BackupStorage | null, slot?: BackupSlot): void {
  const s = resolveStorage(storage);
  if (!s) return;
  const slots: BackupSlot[] = slot ? [slot] : ['main', 'pending'];
  for (const sl of slots) {
    try {
      s.removeItem(quoteDraftBackupKey(quoteId, sl));
    } catch {
      /* Sicherung ist best effort. */
    }
  }
}

/** Die juengere der beiden Sicherungen eines Angebots (nach savedAt). */
export function readNewestQuoteDraftBackup(
  quoteId: string,
  storage?: BackupStorage | null,
): QuoteDraftBackup | null {
  const s = resolveStorage(storage);
  const main = readQuoteDraftBackup(quoteId, s, 'main');
  const pending = readQuoteDraftBackup(quoteId, s, 'pending');
  if (!main) return pending;
  if (!pending) return main;
  return Date.parse(pending.savedAt) > Date.parse(main.savedAt) ? pending : main;
}

/**
 * Die Sicherung setzt auf einem anderen Serverstand auf als dem jetzigen (oder
 * ist eine Altsicherung ohne Ausgangsstand): das Angebot wurde seitdem
 * geaendert. Das ist ein HINWEIS beim Wiederherstellen — nie ein Grund, die
 * Sicherung wegzuwerfen: ein neuerer Serverstand beweist nicht, dass die
 * lokalen Aenderungen gespeichert wurden.
 */
export function backupBaseIsStale(
  backup: Pick<QuoteDraftBackup, 'base' | 'baseUpdatedAt'>,
  serverUpdatedAt: string | null | undefined,
): boolean {
  return !backup.base || backup.baseUpdatedAt !== (serverUpdatedAt ?? null);
}

const stable = (v: unknown): string => {
  if (v === undefined) return 'u';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  const rec = v as Record<string, unknown>;
  return `{${Object.keys(rec)
    .filter((k) => rec[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stable(rec[k])}`)
    .join(',')}}`;
};

/** Inhaltsstand eines Entwurfs (nur die editierbaren Felder), als kurzer Hash. */
export function quoteDraftRevision(draft: Quote): string {
  const text = stable(QUOTE_EDITABLE_KEYS.map((k) => draft[k]));
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return `${text.length.toString(36)}-${h.toString(36)}`;
}

/** Was ein erfolgreiches Speichern nachweislich abgedeckt hat. */
export interface SaveCoverage {
  /** Der abgeschickte Entwurf. */
  sent: Quote;
  /** Sitzung des speichernden Editors. */
  sessionId: string;
  /** Hoechste Sicherungsnummer dieser Sitzung beim START des Speicherns. */
  seqAtStart: number;
}

/**
 * Darf die gespeicherte Sicherung nach diesem erfolgreichen Speichern weg?
 *  - Ihr Inhalt ist genau der abgeschickte Entwurf, oder
 *  - sie stammt aus DERSELBEN Editor-Sitzung und wurde vor dem Start dieses
 *    Speicherns geschrieben (ein Vorgaenger des abgeschickten Stands).
 * Eine juengere Sicherung (waehrend des Speicherns weitergetippt, oder der
 * eingefrorene Stand beim Angebotswechsel) und die Sicherung einer ANDEREN
 * Sitzung (z. B. offline geschlossen, nie gespeichert) bleiben.
 */
export function isBackupCoveredBySave(stored: QuoteDraftBackup, cover: SaveCoverage): boolean {
  if (stored.quoteId !== cover.sent.id) return false;
  if (quoteDraftRevision(stored.draft) === quoteDraftRevision(cover.sent)) return true;
  return stored.sessionId === cover.sessionId
    && typeof stored.seq === 'number'
    && stored.seq <= cover.seqAtStart;
}

/** Sicherung nur entfernen, wenn das Speichern sie abgedeckt hat. true = entfernt. */
export function clearQuoteDraftBackupIfCovered(
  cover: SaveCoverage,
  storage?: BackupStorage | null,
): boolean {
  const s = resolveStorage(storage);
  let cleared = false;
  for (const slot of ['main', 'pending'] as const) {
    const stored = readQuoteDraftBackup(cover.sent.id, s, slot);
    if (stored && isBackupCoveredBySave(stored, cover)) {
      clearQuoteDraftBackup(cover.sent.id, s, slot);
      cleared = true;
    }
  }
  return cleared;
}

export function buildQuoteDraftBackup(
  draft: Quote,
  base: Quote,
  now: Date = new Date(),
  session?: { sessionId: string; seq: number },
): QuoteDraftBackup {
  return {
    quoteId: draft.id,
    baseUpdatedAt: base.updatedAt ?? null,
    draft,
    base,
    savedAt: now.toISOString(),
    ...(session ? { sessionId: session.sessionId, seq: session.seq } : {}),
  };
}
